import {
  Box,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  useCustomToast,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { ICrmDedDetailBusiness, useGetCrmDedDetailsApi, useGetDedDetailByLicenseNumberApi } from 'apis/crm-ded-details';
import { BaseTableContainer } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle, ClearFilterButton } from 'components/base-table/components';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiGrid, FiList } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { BusinessNameFilterButton } from '../components/filters/business-name-filter';
import { LicenseNumberFilterButton } from '../components/filters/license-number-filter';
import { ViewDetailModal } from '../components/modals/view-detail-modal';
import { StatsCards } from '../components/stats-cards';
import { EnhancedLicenseCell } from '../components/table-cells/enhanced-license-cell';

interface FlattenedLicense {
  id: number;
  licenseNumber: string;
  businessName: string;
  licenseStatus: string;
  expiryDate?: string;
  legalType?: string;
  isActive: boolean;
  isExpiringSoon: boolean;
  hasCompleteContactInfo: boolean;
  hasCompleteAddress: boolean;
  area?: string;
  activities?: string;
  licenseIssuedAt?: string;
  businessId?: string;
}

const CrmDedDetailsEnhancedListScreen = () => {
  const dispatch = useAppDispatch();
  const toast = useCustomToast();
  const history = useHistory();
  const { getPageParams } = usePageParams();
  const params = getPageParams();

  // View mode state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Modals state
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState<string>('');

  // API hooks
  const { isLoading, isFetching, data } = useGetCrmDedDetailsApi({
    onError: (err) => {
      console.error('🚨 DED Details API Error:', err);
      toast({
        title: 'Failed to Load Data',
        description: 'Unable to load DED details. Please check your connection and try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  // Single license lookup if filtering by license number
  const { data: singleLicenseData, isLoading: isSingleLicenseLoading } = useGetDedDetailByLicenseNumberApi(
    String(params.licenseNumber || ''),
    {
      enabled: !!params.licenseNumber,
      onError: (err) => {
        console.error('🚨 Single License API Error:', err);
        toast({
          title: 'License Not Found',
          description: `Unable to find license: ${params.licenseNumber}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  // Flatten all licenses from grouped businesses
  const flattenedLicenses: FlattenedLicense[] = useMemo(() => {
    // If filtering by specific license number, return single license
    if (params.licenseNumber && singleLicenseData) {
      return [
        {
          id: singleLicenseData.id,
          licenseNumber: singleLicenseData.licenseNumber,
          businessName: singleLicenseData.businessName,
          licenseStatus: singleLicenseData.licenseStatus,
          expiryDate: singleLicenseData.expiryDate,
          legalType: singleLicenseData.legalType,
          isActive: singleLicenseData.isActive,
          isExpiringSoon: singleLicenseData.isExpiringSoon,
          hasCompleteContactInfo: singleLicenseData.hasCompleteContactInfo,
          hasCompleteAddress: singleLicenseData.hasCompleteAddress,
          area: singleLicenseData.area,
          activities: singleLicenseData.activities,
          licenseIssuedAt: singleLicenseData.licenseIssuedAt,
        },
      ];
    }

    // Otherwise, flatten all licenses from grouped data
    if (!data?.data) return [];

    const licenses: FlattenedLicense[] = [];
    data.data.forEach((business: ICrmDedDetailBusiness) => {
      business.licenses.forEach((license) => {
        licenses.push({
          id: license.id,
          licenseNumber: license.licenseNumber,
          businessName: business.businessName,
          licenseStatus: license.licenseStatus,
          expiryDate: license.expiryDate,
          legalType: license.legalType,
          isActive: license.licenseStatus?.toLowerCase().includes('active') || false,
          isExpiringSoon: license.isExpiringSoon,
          hasCompleteContactInfo: !!(
            license.contactInfo?.telephone ||
            license.contactInfo?.mobile ||
            license.contactInfo?.email
          ),
          hasCompleteAddress: !!(license.location?.address && license.location?.area),
          area: license.location?.area,
          activities: license.activities,
          licenseIssuedAt: license.createdAt, // Fallback to created date
          businessId: business.businessId,
        });
      });
    });

    // Sort by active status first, then by expiry date
    return licenses.sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      if (a.isExpiringSoon && !b.isExpiringSoon) return -1;
      if (!a.isExpiringSoon && b.isExpiringSoon) return 1;
      return 0;
    });
  }, [data, singleLicenseData, params.licenseNumber]);

  const isDataLoading = isLoading || isSingleLicenseLoading;
  const isDataFetching = isFetching;

  // Handlers
  const handleViewDetail = (licenseId: number, businessName: string) => {
    setSelectedDetailId(licenseId);
    setSelectedBusinessName(businessName);
    onViewOpen();
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM DED Details - Enhanced View'));
  }, [dispatch]);

  const totalLicenses = flattenedLicenses.length;
  const activeLicenses = flattenedLicenses.filter((l) => l.isActive).length;
  const expiringSoon = flattenedLicenses.filter((l) => l.isExpiringSoon).length;

  return (
    <>
      {/* Statistics Cards */}
      <StatsCards />

      <BaseTableContainer isLoading={isDataLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='DED Trade Licenses' />
            <BaseTableSpinner isFetching={isDataFetching} />

            {/* Quick Stats */}
            <HStack spacing={4} ml={4}>
              <Text fontSize='sm' color='gray.600'>
                📄 {totalLicenses} licenses
              </Text>
              <Text fontSize='sm' color='green.600'>
                ✅ {activeLicenses} active
              </Text>
              {expiringSoon > 0 && (
                <Text fontSize='sm' color='orange.600'>
                  ⚠️ {expiringSoon} expiring soon
                </Text>
              )}
            </HStack>
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <HStack spacing={3}>
              {/* Back to Business View */}
              <Tooltip label='Back to Business View'>
                <IconButton
                  aria-label='Back to Business View'
                  icon={<FiArrowLeft />}
                  size='sm'
                  colorScheme='gray'
                  variant='outline'
                  onClick={() => history.push('/app/crm-ded-details/list')}
                />
              </Tooltip>

              {/* View Mode Toggle */}
              <HStack spacing={1}>
                <Tooltip label='Grid view'>
                  <IconButton
                    aria-label='Grid view'
                    icon={<FiGrid />}
                    size='sm'
                    variant={viewMode === 'grid' ? 'solid' : 'outline'}
                    colorScheme='blue'
                    onClick={() => setViewMode('grid')}
                  />
                </Tooltip>
                <Tooltip label='List view'>
                  <IconButton
                    aria-label='List view'
                    icon={<FiList />}
                    size='sm'
                    variant={viewMode === 'list' ? 'solid' : 'outline'}
                    colorScheme='blue'
                    onClick={() => setViewMode('list')}
                  />
                </Tooltip>
              </HStack>

              {/* Filters */}
              <LicenseNumberFilterButton />
              <BusinessNameFilterButton />
              <ClearFilterButton />
            </HStack>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          {flattenedLicenses.length === 0 ? (
            <Flex justify='center' align='center' minH='200px'>
              <VStack spacing={3}>
                <Text color='gray.500' fontSize='lg'>
                  {params.licenseNumber ? 'License not found' : 'No licenses available'}
                </Text>
                <Text color='gray.400' fontSize='sm'>
                  {params.licenseNumber
                    ? `No license found with number: ${params.licenseNumber}`
                    : 'Try adjusting your filters or check back later'}
                </Text>
              </VStack>
            </Flex>
          ) : (
            <Box p={4}>
              <SimpleGrid
                columns={{
                  base: 1,
                  md: viewMode === 'grid' ? 2 : 1,
                  lg: viewMode === 'grid' ? 3 : 1,
                  xl: viewMode === 'grid' ? 4 : 1,
                }}
                spacing={4}>
                {flattenedLicenses.map((license) => (
                  <Box
                    key={`${license.licenseNumber}-${license.id}`}
                    onClick={() => handleViewDetail(license.id, license.businessName)}
                    cursor='pointer'
                    transition='transform 0.2s'
                    _hover={{ transform: 'translateY(-2px)' }}>
                    <EnhancedLicenseCell
                      licenseNumber={license.licenseNumber}
                      businessName={license.businessName}
                      licenseStatus={license.licenseStatus}
                      expiryDate={license.expiryDate}
                      legalType={license.legalType}
                      isActive={license.isActive}
                      isExpiringSoon={license.isExpiringSoon}
                      hasCompleteContactInfo={license.hasCompleteContactInfo}
                      hasCompleteAddress={license.hasCompleteAddress}
                      area={license.area}
                      activities={license.activities}
                      licenseIssuedAt={license.licenseIssuedAt}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </BaseTableContainer.TableWrapper>

        {/* Footer with pagination if needed */}
        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left>
            <Text fontSize='sm' color='gray.600'>
              Showing {flattenedLicenses.length} license{flattenedLicenses.length !== 1 ? 's' : ''}
              {params.licenseNumber && ' (filtered by license number)'}
            </Text>
          </BaseTableContainer.Footer.Left>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {/* Modals */}
      {selectedDetailId && (
        <ViewDetailModal
          isOpen={isViewOpen}
          onClose={onViewClose}
          detailId={selectedDetailId}
          businessName={selectedBusinessName}
        />
      )}
    </>
  );
};

export default CrmDedDetailsEnhancedListScreen;
